import { Spinner, useToast } from '@chakra-ui/react'
import type React from 'react'
import { useEffect, useState } from 'react'
import DefaultModal from '../../../../../../components/DefaultModal'
import Toast from '../../../../../../components/Toast'
import {
  type AdminWealthManagerDto,
  usePreviewWealthManagerRatingChangeMutation,
  useSetWealthManagerRiskRatingMutation,
} from '../../../../../../redux/services/coreApi'
import type {
  ComplianceCaseType,
  RescorePreviewDto,
  RiskTier,
} from '../../../../../../types/coreApi-types'

interface RiskRatingModalProps {
  isOpen: boolean
  onClose: () => void
  wealthManager?: AdminWealthManagerDto | null
}

// "Unset" is represented as null on the wire; the radio set offers it explicitly so an admin can clear a
// previously-set rating.
const OPTIONS: { label: string; value: RiskTier | null }[] = [
  { label: 'Low', value: 'Low' },
  { label: 'Medium', value: 'Medium' },
  { label: 'High', value: 'High' },
  { label: 'Unset', value: null },
]

// Editor for a WM's admin-attested risk rating. The rating feeds a client's D3 introducer score (v7.8), so
// changing it must propagate to the WM's already-scored clients. The change is preview-gated and atomic
// (ADR-0007 amendment): Save scores the WM's book against the proposed rating and shows what would change;
// Confirm sets the rating AND re-scores the book together; Cancel discards (nothing is written by a
// preview). A WM with no scored clients just persists the rating.
const RiskRatingModal: React.FC<RiskRatingModalProps> = ({
  isOpen,
  onClose,
  wealthManager,
}) => {
  const toast = useToast()
  const [selected, setSelected] = useState<RiskTier | null>(null)
  const [preview, setPreview] = useState<RescorePreviewDto | null>(null)
  const [previewRatingChange, { isLoading: isPreviewing }] =
    usePreviewWealthManagerRatingChangeMutation()
  const [setRiskRating, { isLoading: isCommitting }] =
    useSetWealthManagerRiskRatingMutation()

  // Reset to the edit step whenever the modal (re)opens.
  useEffect(() => {
    if (isOpen) {
      setSelected(wealthManager?.riskRating ?? null)
      setPreview(null)
    }
  }, [isOpen, wealthManager])

  const onPreview = async () => {
    if (!wealthManager) return
    try {
      const result = await previewRatingChange({
        id: wealthManager.id,
        riskRating: selected,
      }).unwrap()
      setPreview(result)
    } catch {
      toast({
        position: 'top-right',
        render: () => (
          <Toast type="error">
            Failed to preview the rating change. Please try again.
          </Toast>
        ),
      })
    }
  }

  const onConfirm = async () => {
    if (!wealthManager) return
    try {
      const result = await setRiskRating({
        id: wealthManager.id,
        riskRating: selected,
      }).unwrap()

      toast({
        position: 'top-right',
        render: () => (
          <Toast type="success">
            Risk rating updated
            {result.rescoredClients > 0
              ? ` — ${result.rescoredClients} client${
                  result.rescoredClients === 1 ? '' : 's'
                } re-scored.`
              : '.'}
          </Toast>
        ),
      })
      onClose()
    } catch {
      toast({
        position: 'top-right',
        render: () => (
          <Toast type="error">
            Failed to update the risk rating. Please try again.
          </Toast>
        ),
      })
    }
  }

  const name =
    [wealthManager?.firstName, wealthManager?.lastName]
      .filter(Boolean)
      .join(' ') || wealthManager?.email

  return (
    <DefaultModal
      isOpen={isOpen}
      onClose={onClose}
      isLoading={false}
      title={preview ? 'Confirm rating change' : 'Set risk rating'}
      whiteBG
      maxW="520px"
    >
      <div className="p-5 pt-0 space-y-5">
        {preview ? (
          <RatingChangePreview preview={preview} name={name} />
        ) : (
          <>
            <p className="text-sm color-text-description">
              Set the admin-attested risk rating for{' '}
              <span className="color-text-default font-semi">{name}</span>. The
              rating feeds each client's risk score, so saving it re-scores this
              manager's already-onboarded clients — you'll preview the impact
              before anything is applied.
            </p>

            <div className="flex flex-col gap-2">
              {OPTIONS.map(option => {
                const isSelected = selected === option.value
                return (
                  <button
                    key={option.label}
                    type="button"
                    onClick={() => setSelected(option.value)}
                    className={`flex items-center justify-between rounded-lg border px-4 py-3 text-left transition-colors ${
                      isSelected
                        ? 'border-primary bg-primary/10'
                        : 'border-gray-200 dark:border-gray-700 hover:border-primary/50'
                    }`}
                  >
                    <span className="color-text-default font-semi">
                      {option.label}
                    </span>
                    {isSelected && (
                      <span className="text-primary text-sm">Selected</span>
                    )}
                  </button>
                )
              })}
            </div>
          </>
        )}

        <div className="flex gap-3 pt-2">
          <button
            className="button-secondary flex-1"
            type="button"
            onClick={preview ? () => setPreview(null) : onClose}
            disabled={isPreviewing || isCommitting}
          >
            {preview ? 'Back' : 'Cancel'}
          </button>
          {preview ? (
            <button
              className="button flex-1"
              type="button"
              disabled={isCommitting}
              onClick={onConfirm}
            >
              {isCommitting ? <Spinner size="sm" /> : 'Confirm & apply'}
            </button>
          ) : (
            <button
              className="button flex-1"
              type="button"
              disabled={isPreviewing}
              onClick={onPreview}
            >
              {isPreviewing ? <Spinner size="sm" /> : 'Save'}
            </button>
          )}
        </div>
      </div>
    </DefaultModal>
  )
}

// Compact would-change summary for the WM's book, shown between Save and Confirm. Mirrors the bulk
// rescore preview (RuleSetsScreen) but scoped to one manager's clients. An empty book is the common case
// for a newly-rated manager — say so explicitly rather than showing empty sections.
const RatingChangePreview: React.FC<{
  preview: RescorePreviewDto
  name?: string
}> = ({ preview, name }) => {
  if (preview.total === 0) {
    return (
      <p className="text-sm color-text-description">
        <span className="color-text-default font-semi">{name}</span> has no
        already-scored clients, so this just sets the rating — there is nothing
        to re-score.
      </p>
    )
  }

  return (
    <div className="space-y-3">
      <p className="text-sm color-text-description">
        Re-scoring{' '}
        <span className="color-text-default font-semi">{preview.total}</span>{' '}
        scored client{preview.total === 1 ? '' : 's'} of{' '}
        <span className="color-text-default font-semi">{name}</span> under the
        proposed rating would change{' '}
        <span className="color-text-default font-semi">{preview.changed}</span>{' '}
        of them. Nothing is written until you confirm.
      </p>

      {preview.tierTransitions.length > 0 && (
        <Section title="Risk-tier changes">
          {preview.tierTransitions.map((t, i) => (
            <CountRow
              key={i}
              label={`${tierLabel(t.from)} → ${tierLabel(t.to)}`}
              value={t.count}
            />
          ))}
        </Section>
      )}

      {preview.stateTransitions.length > 0 && (
        <Section title="State changes">
          {preview.stateTransitions.map((s, i) => (
            <CountRow
              key={i}
              label={`${labelize(s.from)} → ${labelize(s.to)}`}
              value={s.count}
            />
          ))}
        </Section>
      )}

      {preview.casesToOpen.length > 0 && (
        <Section title="Cases that would open">
          {preview.casesToOpen.map((c, i) => (
            <CountRow
              key={i}
              label={`${caseTypeLabel(c.caseType)} (${c.assignedRole.toUpperCase()})`}
              value={c.count}
            />
          ))}
        </Section>
      )}

      {(preview.restrictionsToApply.length > 0 ||
        preview.restrictionsToLift.length > 0) && (
        <Section title="Restrictions">
          {preview.restrictionsToApply.map((r, i) => (
            <CountRow
              key={`apply-${i}`}
              label={`Apply — ${labelize(r.kind)}`}
              value={r.count}
            />
          ))}
          {preview.restrictionsToLift.map((r, i) => (
            <CountRow
              key={`lift-${i}`}
              label={`Lift — ${labelize(r.kind)}`}
              value={r.count}
            />
          ))}
        </Section>
      )}

      {preview.changed > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left color-text-description">
                <th className="py-1 pr-4 font-semi">Client</th>
                <th className="py-1 pr-4 font-semi">Tier</th>
                <th className="py-1 font-semi">Case</th>
              </tr>
            </thead>
            <tbody>
              {preview.changedInvestors.map(r => (
                <tr key={r.investorId} className="align-top">
                  <td className="py-1 pr-4">
                    {r.name ?? (
                      <span className="font-mono text-xs">{r.investorId}</span>
                    )}
                  </td>
                  <td className="py-1 pr-4">
                    {r.oldTier !== r.newTier ? (
                      <span>
                        {tierLabel(r.oldTier)} →{' '}
                        <strong>{tierLabel(r.newTier)}</strong>
                      </span>
                    ) : (
                      tierLabel(r.newTier)
                    )}
                  </td>
                  <td className="py-1">
                    {r.caseToOpen ? (
                      `${caseTypeLabel(r.caseToOpen)} (${r.caseAssignedRole?.toUpperCase()})`
                    ) : (
                      <span className="color-text-description">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

const tierLabel = (tier?: RiskTier | null): string => tier ?? 'None'

const labelize = (value: string): string =>
  value.replace(/([a-z])([A-Z])/g, '$1 $2')

const caseTypeLabel = (type: ComplianceCaseType): string => labelize(type)

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div>
    <h3 className="font-semi text-sm mb-1">{title}</h3>
    {children}
  </div>
)

const CountRow: React.FC<{ label: string; value: number }> = ({
  label,
  value,
}) => (
  <div className="flex items-center justify-between text-sm">
    <span>{label}</span>
    <span className="font-semi">{value}</span>
  </div>
)

export default RiskRatingModal
